import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid2,
} from "@mui/material";
import FormTitle from "../../components/helpers/FormTitle";
import * as yup from "yup";
import { useFormik } from "formik";
import { _ACTIONS, _ROLES_BACK } from "../../utils/constants";
import AppRights from "../../services/AppRights";
import { getOptionsSelectFiscalYear } from "../../utils/function";
import { useEffect, useState } from "react";
import { _ROUTES } from "../../utils/_routes_constants";
import { DataService } from "../../services/DataService";
import { createToast } from "../../utils/toast";
import { createWorksheet } from "../../components/budget/export/worksheetRules";

export const ExportBudget = () => {
  const [activities, setActivities] = useState([]);
  const [sites, setSites] = useState([]);
  const dataService = new DataService();

  const validationSchema = yup.object({
    fiscalYear: yup.string().required("Fiscal year is required"),
    version: yup
      .number("Version must be a number")
      .required("Version is required"),
    activity: yup.string().required("Activity is required"),
    site: yup.string().required("Site is required"),
  });

  const formik = useFormik({
    initialValues: {
      fiscalYear: "",
      version: "",
      activity: "",
      site: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      dataService.doRequest(
        _ACTIONS.GET,
        _ROUTES.EXPORT_BUDGET,
        values,
        {},
        async (data) => {
          if (data.length === 0) {
            return createToast("warning", "No data to export");
          }
          const site = sites.find((s) => s.id === parseInt(values.site));
          const activity = activities.find(
            (a) => a.id === parseInt(values.activity),
          );

          return createWorksheet(
            data,
            values.version,
            activity.code,
            site.code,
          );
        },
      );
    },
  });

  const yearOptions = getOptionsSelectFiscalYear();

  useEffect(() => {
    if (activities.length === 0) {
      dataService.fetchData(
        _ROUTES.ACTIVITIES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
        },
        (data) => {
          setActivities(data);
        },
      );
    }
    if (sites.length === 0) {
      dataService.fetchData(
        _ROUTES.SITES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
        },
        (data) => {
          setSites(data);
        },
      );
    }
  }, []);

  const columnSize = { xs: 12, sm: 6, md: 2 };
  const buttonOffset = { xs: 0, sm: 0, md: 0.5 };

  return (
    <>
      <AppRights roles={[_ROLES_BACK._ROLE_ADMIN, _ROLES_BACK._ROLE_CTRL]} />
      <FormTitle title="Export Budget" />
      <Box
        sx={{
          width: "100%",
          border: "1px solid black",
          padding: { xs: 2, sm: 3, md: 4 },
          marginTop: 5,
          borderRadius: 3,
        }}
      >
        <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
          <Grid2 container spacing={5}>
            {/* Fiscal Year */}
            <Grid2 size={columnSize}>
              <FormControl
                fullWidth
                error={
                  formik.touched.fiscalYear && Boolean(formik.errors.fiscalYear)
                }
              >
                <InputLabel id="fiscalYear-label" size="small">
                  Fiscal Year
                </InputLabel>
                <Select
                  labelId="fiscalYear-label"
                  id="fiscalYear"
                  name="fiscalYear"
                  value={formik.values.fiscalYear}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="small"
                >
                  {yearOptions.map((option, i) => (
                    <MenuItem key={i} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.fiscalYear && formik.errors.fiscalYear && (
                  <FormHelperText>{formik.errors.fiscalYear}</FormHelperText>
                )}
              </FormControl>
            </Grid2>

            {/* Version */}
            <Grid2 size={columnSize}>
              <TextField
                fullWidth
                id="version"
                name="version"
                label="Version"
                size="small"
                helperText={formik.touched.version && formik.errors.version}
                error={formik.touched.version && Boolean(formik.errors.version)}
                value={formik.values.version}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Grid2>

            {/* Activity */}
            <Grid2 size={columnSize}>
              <FormControl
                fullWidth
                error={
                  formik.touched.activity && Boolean(formik.errors.activity)
                }
              >
                <InputLabel id="activity-label" size="small">
                  Activity
                </InputLabel>
                <Select
                  labelId="activity-label"
                  id="activity"
                  name="activity"
                  value={formik.values.activity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="small"
                >
                  {activities.map((option, i) => (
                    <MenuItem key={i} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.activity && formik.errors.activity && (
                  <FormHelperText>{formik.errors.activity}</FormHelperText>
                )}
              </FormControl>
            </Grid2>

            {/* Site */}
            <Grid2 size={columnSize}>
              <FormControl
                fullWidth
                error={formik.touched.site && Boolean(formik.errors.site)}
              >
                <InputLabel id="site-label" size="small">
                  Site
                </InputLabel>
                <Select
                  labelId="site-label"
                  id="site"
                  name="site"
                  value={formik.values.site}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  size="small"
                >
                  {sites.map((option, i) => (
                    <MenuItem key={i} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.site && formik.errors.site && (
                  <FormHelperText>{formik.errors.site}</FormHelperText>
                )}
              </FormControl>
            </Grid2>

            {/* Export Button */}
            <Grid2
              size={columnSize}
              offset={buttonOffset}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Button
                color="success"
                variant="contained"
                type="submit"
                sx={{
                  width: "70%",
                }}
              >
                Export
              </Button>
            </Grid2>
          </Grid2>
        </form>
      </Box>
    </>
  );
};
